const PICKUP_CACHE_DURATION = 6 * 60 * 60 * 1000;
const PICKUP_LOAD_TIMEOUT = 12000;

class CartDeliveryOptions extends HTMLElement {
  constructor() {
    super();

    this.locations = [];
    this.locationsLoaded = false;
    this.locationLoadFailed = false;
    this.selectedLocation = null;
    this.isSaving = false;
    this.currentCarrier = 'omniva';

    this.handleMethodChange =
      this.handleMethodChange.bind(this);

    this.handleSearch =
      this.handleSearch.bind(this);

    this.handleDocumentClick =
      this.handleDocumentClick.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';

    this.omnivaEndpoint =
      this.dataset.omnivaEndpoint ||
      this.dataset.endpoint ||
      '';

    this.dpdEndpoint =
      this.dataset.dpdEndpoint ||
      '';

    // Delegated events survive Shopify cart DOM morphing.
    this.addEventListener(
      'change',
      this.handleMethodChange
    );

    this.addEventListener(
      'input',
      this.handleSearch
    );

    this.addEventListener(
      'focusin',
      this.handleSearch
    );

    document.addEventListener(
      'click',
      this.handleDocumentClick
    );

    const selectedMethod =
      this.getSelectedMethod();

    this.currentCarrier =
      selectedMethod === 'dpd'
        ? 'dpd'
        : 'omniva';

    const searchInput =
      this.getElement('searchInput');

    // If Liquid restored a previously selected pickup point,
    // treat it as selected so checkout is not incorrectly blocked.
    if (searchInput?.value?.trim()) {
      this.selectedLocation = {
        pickup_point:
          searchInput.value.trim(),
        city: '',
        zip: '',
        address: '',
        comment: '',
        latitude: '',
        longitude: '',
      };
    }

    this.updateInterface();

    if (
      selectedMethod === 'omniva' ||
      selectedMethod === 'dpd'
    ) {
      this.loadLocations();
    }
  }

  disconnectedCallback() {
    this.removeEventListener(
      'change',
      this.handleMethodChange
    );

    this.removeEventListener(
      'input',
      this.handleSearch
    );

    this.removeEventListener(
      'focusin',
      this.handleSearch
    );

    document.removeEventListener(
      'click',
      this.handleDocumentClick
    );
  }

  getElement(name) {
    const selectors = {
      searchInput:
        '[data-pickup-search]',

      resultsContainer:
        '[data-pickup-results]',

      loadingElement:
        '[data-pickup-loading]',

      errorElement:
        '[data-pickup-error]',

      pickupContainer:
        '.cart-delivery-options__pickup',

      pickupTitle:
        '[data-pickup-title]',
    };

    return this.querySelector(
      selectors[name]
    );
  }

  getMethodInputs() {
    return Array.from(
      this.querySelectorAll(
        'input[name="cart_delivery_method"]'
      )
    );
  }

  findCheckoutButton() {
    const summary =
      this.closest(
        '.cart-drawer__summary'
      );

    return (
      summary?.querySelector(
        '#checkout'
      ) ||
      summary?.querySelector(
        '[name="checkout"]'
      ) ||
      document.querySelector(
        '#cart-drawer #checkout'
      )
    );
  }

  findAcceleratedCheckout() {
    const summary =
      this.closest(
        '.cart-drawer__summary'
      );

    return (
      summary?.querySelector(
        '.additional-checkout-buttons'
      ) || null
    );
  }

  getSelectedMethod() {
    return (
      this.getMethodInputs().find(
        (input) => input.checked
      )?.value || 'pickup'
    );
  }

  clearLocationUI() {
    const searchInput =
      this.getElement('searchInput');

    const resultsContainer =
      this.getElement('resultsContainer');

    if (searchInput) {
      searchInput.value = '';
    }

    if (resultsContainer) {
      resultsContainer.innerHTML = '';
      resultsContainer.hidden = true;
    }

    this.selectedLocation = null;
  }

  async handleMethodChange(event) {
    if (
      event.target?.name !==
      'cart_delivery_method'
    ) {
      return;
    }

    const method =
      this.getSelectedMethod();

    this.clearLocationUI();

    this.locationLoadFailed = false;
    this.locationsLoaded = false;
    this.locations = [];

    if (method === 'pickup') {
      this.currentCarrier = 'omniva';

      this.setLoading(false);
      this.hideError();
      this.updateInterface();

      // Store pickup can be saved immediately.
      this.saveCartAttributes({
        delivery_method: 'pickup',
        pickup_carrier: '',

        omniva_location: '',
        omniva_city: '',
        omniva_address: '',
        omniva_terminal_id: '',
        omniva_comment: '',
        omniva_latitude: '',
        omniva_longitude: '',

        dpd_location: '',
        dpd_city: '',
        dpd_address: '',
        dpd_terminal_id: '',
        dpd_comment: '',
        dpd_latitude: '',
        dpd_longitude: '',
      });

      return;
    }

    // Omniva or DPD
    this.currentCarrier = method;

    // Show pickup UI immediately.
    this.updateInterface();

    // Load the correct carrier's locations.
    await this.loadLocations();

    if (!this.isConnected) return;

    this.renderResults(
      this.locations.slice(0, 30)
    );

    this.getElement(
      'searchInput'
    )?.focus();

    this.updateInterface();
  }

  getEndpoint() {
    return this.currentCarrier === 'dpd'
      ? this.dpdEndpoint
      : this.omnivaEndpoint;
  }

  getCacheKey() {
    return `pickup-locations-${this.currentCarrier}-v3`;
  }

  async loadLocations() {
    if (this.locationsLoaded) {
      this.setLoading(false);

      this.renderResults(
        this.locations.slice(0, 30)
      );

      this.updateInterface();

      return;
    }

    const endpoint =
      this.getEndpoint();

    if (!endpoint) {
      this.locationLoadFailed = true;
      this.setLoading(false);

      this.showError(
        this.currentCarrier === 'dpd'
          ? 'DPD pakomātu fails nav atrasts.'
          : 'Omniva pakomātu fails nav atrasts.'
      );

      this.updateInterface();

      return;
    }

    this.locationLoadFailed = false;
    this.setLoading(true);
    this.hideError();

    try {
      const cached =
        this.readCache();

      if (cached) {
        this.locations = cached;
        this.locationsLoaded = true;

        this.setLoading(false);

        this.renderResults(
          this.locations.slice(0, 30)
        );

        this.updateInterface();

        return;
      }

      const controller =
        new AbortController();

      const timeoutId =
        window.setTimeout(
          () => controller.abort(),
          PICKUP_LOAD_TIMEOUT
        );

      let response;

      try {
        response = await fetch(
          endpoint,
          {
            method: 'GET',
            headers: {
              Accept:
                'application/json',
            },
            cache: 'no-store',
            signal:
              controller.signal,
          }
        );
      } finally {
        window.clearTimeout(
          timeoutId
        );
      }

      if (!response.ok) {
        throw new Error(
          `${this.currentCarrier.toUpperCase()} JSON request failed: ${response.status}`
        );
      }

      const data =
        await response.json();

      if (!Array.isArray(data)) {
        throw new Error(
          `${this.currentCarrier.toUpperCase()} JSON response is not an array.`
        );
      }

      this.locations =
        data
          .filter((location) => {
            return (
              location &&
              location.TYPE === '0' &&
              typeof location.NAME ===
                'string'
            );
          })
          .map((location) => {
            const city =
              location.A2_NAME ||
              location.A3_NAME ||
              location.A1_NAME ||
              '';

            const address =
              [
                location.A5_NAME,
                location.A7_NAME,
              ]
                .filter(Boolean)
                .join(' ');

            return {
              pickup_point:
                location.NAME,
              city,
              zip:
                location.ZIP || '',
              address,
              comment:
                location.comment_lav ||
                '',
              latitude:
                location.Y_COORDINATE ||
                '',
              longitude:
                location.X_COORDINATE ||
                '',
            };
          })
          .sort((a, b) => {
            return (
              `${a.city} ${a.pickup_point}`
            ).localeCompare(
              `${b.city} ${b.pickup_point}`,
              'lv'
            );
          });

      if (this.locations.length === 0) {
        throw new Error(
          `No ${this.currentCarrier} pickup locations were found.`
        );
      }

      this.locationsLoaded = true;

      this.writeCache(
        this.locations
      );

      this.renderResults(
        this.locations.slice(0, 30)
      );

    } catch (error) {
      console.error(
        '[cart-delivery-options]',
        error
      );

      this.locationLoadFailed = true;

      this.showError(
        this.currentCarrier === 'dpd'
          ? 'Neizdevās ielādēt DPD pakomātus. Lūdzu, mēģini vēlreiz.'
          : 'Neizdevās ielādēt Omniva pakomātus. Lūdzu, mēģini vēlreiz.'
      );

    } finally {
      this.setLoading(false);
      this.updateInterface();
    }
  }

  handleSearch(event) {
    const searchInput =
      this.getElement(
        'searchInput'
      );

    if (
      event &&
      event.target !== searchInput
    ) {
      return;
    }

    const resultsContainer =
      this.getElement(
        'resultsContainer'
      );

    if (
      !searchInput ||
      !resultsContainer
    ) {
      return;
    }

    const pickupContainer =
      this.getElement(
        'pickupContainer'
      );

    if (pickupContainer?.hidden) {
      return;
    }

    if (!this.locationsLoaded) {
      return;
    }

    const query =
      this.normalizeText(
        searchInput.value.trim()
      );

    if (!query) {
      this.renderResults(
        this.locations.slice(0, 30)
      );

      return;
    }

    const matches =
      this.locations
        .filter((location) => {
          const searchableText =
            this.normalizeText(
              [
                location.city,
                location.pickup_point,
                location.address,
                location.zip,
              ]
                .filter(Boolean)
                .join(' ')
            );

          return searchableText.includes(
            query
          );
        })
        .slice(0, 50);

    this.renderResults(
      matches
    );
  }

  renderResults(locations) {
    const resultsContainer =
      this.getElement(
        'resultsContainer'
      );

    if (!resultsContainer) {
      return;
    }

    resultsContainer.innerHTML =
      '';

    if (!locations.length) {
      const empty =
        document.createElement(
          'span'
        );

      empty.className =
        'cart-delivery-options__empty';

      empty.textContent =
        'Pakomāti nav atrasti.';

      resultsContainer.appendChild(
        empty
      );

      resultsContainer.hidden =
        false;

      return;
    }

    const fragment =
      document.createDocumentFragment();

    locations.forEach(
      (location) => {
        const button =
          document.createElement(
            'button'
          );

        button.type = 'button';

        button.className =
          'cart-delivery-options__result';

        const name =
          document.createElement(
            'strong'
          );

        name.textContent =
          location.pickup_point;

        const details =
          document.createElement(
            'span'
          );

        details.className =
          'cart-delivery-options__result-city';

        details.textContent =
          [
            location.city,
            location.address,
            location.zip
              ? `ID ${location.zip}`
              : '',
          ]
            .filter(Boolean)
            .join(' · ');

        button.append(
          name,
          details
        );

        button.addEventListener(
          'click',
          () => {
            this.selectLocation(
              location
            );
          }
        );

        fragment.appendChild(
          button
        );
      }
    );

    resultsContainer.appendChild(
      fragment
    );

    resultsContainer.hidden =
      false;
  }

  async selectLocation(
    location
  ) {
    this.selectedLocation =
      location;

    const searchInput =
      this.getElement(
        'searchInput'
      );

    const resultsContainer =
      this.getElement(
        'resultsContainer'
      );

    if (searchInput) {
      searchInput.value =
        location.pickup_point;
    }

    if (resultsContainer) {
      resultsContainer.hidden =
        true;
    }

    this.hideError();

    this.updateInterface();

    if (
      this.currentCarrier ===
      'dpd'
    ) {
      await this.saveCartAttributes({
        delivery_method:
          'delivery',

        pickup_carrier:
          'dpd',

        dpd_location:
          location.pickup_point,

        dpd_city:
          location.city,

        dpd_address:
          location.address,

        dpd_terminal_id:
          location.zip,

        dpd_comment:
          location.comment,

        dpd_latitude:
          location.latitude,

        dpd_longitude:
          location.longitude,

        omniva_location: '',
        omniva_city: '',
        omniva_address: '',
        omniva_terminal_id: '',
        omniva_comment: '',
        omniva_latitude: '',
        omniva_longitude: '',
      });

      return;
    }

    await this.saveCartAttributes({
      delivery_method:
        'delivery',

      pickup_carrier:
        'omniva',

      omniva_location:
        location.pickup_point,

      omniva_city:
        location.city,

      omniva_address:
        location.address,

      omniva_terminal_id:
        location.zip,

      omniva_comment:
        location.comment,

      omniva_latitude:
        location.latitude,

      omniva_longitude:
        location.longitude,

      dpd_location: '',
      dpd_city: '',
      dpd_address: '',
      dpd_terminal_id: '',
      dpd_comment: '',
      dpd_latitude: '',
      dpd_longitude: '',
    });
  }

  updateInterface() {
    const method =
      this.getSelectedMethod();

    const deliverySelected =
      method === 'omniva' ||
      method === 'dpd';

    const hasSelectedLocation =
      Boolean(
        this.selectedLocation
      );

    const checkoutBlocked =
      deliverySelected &&
      !hasSelectedLocation;

    const pickupContainer =
      this.getElement(
        'pickupContainer'
      );

    const pickupTitle =
      this.getElement(
        'pickupTitle'
      );

    const loadingElement =
      this.getElement(
        'loadingElement'
      );

    const errorElement =
      this.getElement(
        'errorElement'
      );

    const checkoutButton =
      this.findCheckoutButton();

    const acceleratedCheckout =
      this.findAcceleratedCheckout();

    if (pickupContainer) {
      pickupContainer.hidden =
        !deliverySelected;
    }

    if (pickupTitle) {
      pickupTitle.textContent =
        method === 'dpd'
          ? 'Izvēlies DPD pakomātu'
          : 'Izvēlies Omniva pakomātu';
    }

    if (checkoutButton) {
      checkoutButton.disabled =
        checkoutBlocked ||
        this.isSaving;

      checkoutButton.setAttribute(
        'aria-disabled',
        String(
          checkoutBlocked ||
          this.isSaving
        )
      );
    }

    if (acceleratedCheckout) {
      acceleratedCheckout.hidden =
        checkoutBlocked ||
        this.isSaving;
    }

    /*
     * Validation text:
     * - Hidden for store pickup.
     * - Hidden while loading.
     * - Hidden if a location is selected.
     * - Hidden if loading failed.
     * - Shown only after locations successfully loaded
     *   and no location has been selected.
     */
    const isLoading =
      loadingElement &&
      !loadingElement.hidden;

    if (!errorElement) {
      return;
    }

    if (
      !deliverySelected ||
      isLoading ||
      this.locationLoadFailed
    ) {
      if (!this.locationLoadFailed) {
        errorElement.hidden =
          true;
      }

      return;
    }

    if (hasSelectedLocation) {
      errorElement.hidden =
        true;

      return;
    }

    if (this.locationsLoaded) {
      errorElement.textContent =
        'Lai turpinātu, izvēlies pakomātu.';

      errorElement.dataset.validation =
        'true';

      errorElement.hidden =
        false;
    } else {
      errorElement.hidden =
        true;
    }
  }

  async saveCartAttributes(
    attributes
  ) {
    this.isSaving = true;

    this.updateInterface();

    try {
      const root =
        window.Shopify?.routes
          ?.root || '/';

      const response =
        await fetch(
          `${root}cart/update.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',
            },

            body:
              JSON.stringify({
                attributes,
              }),
          }
        );

      if (!response.ok) {
        throw new Error(
          `Cart update failed: ${response.status}`
        );
      }

      await response.json();

    } catch (error) {
      console.error(
        '[cart-delivery-options]',
        error
      );

      this.showError(
        'Neizdevās saglabāt piegādes izvēli. Lūdzu, mēģini vēlreiz.'
      );

    } finally {
      this.isSaving = false;

      if (this.isConnected) {
        this.updateInterface();
      }
    }
  }

  showError(message) {
    const errorElement =
      this.getElement(
        'errorElement'
      );

    if (!errorElement) {
      return;
    }

    errorElement.textContent =
      message;

    delete errorElement
      .dataset
      .validation;

    errorElement.dataset
      .requestError =
      'true';

    errorElement.hidden =
      false;
  }

  hideError() {
    const errorElement =
      this.getElement(
        'errorElement'
      );

    if (!errorElement) {
      return;
    }

    delete errorElement
      .dataset
      .requestError;

    delete errorElement
      .dataset
      .validation;

    errorElement.hidden =
      true;
  }

  handleDocumentClick(
    event
  ) {
    const resultsContainer =
      this.getElement(
        'resultsContainer'
      );

    if (
      !this.contains(
        event.target
      ) &&
      resultsContainer
    ) {
      resultsContainer.hidden =
        true;
    }
  }

  setLoading(
    isLoading
  ) {
    const loadingElement =
      this.getElement(
        'loadingElement'
      );

    if (loadingElement) {
      loadingElement.hidden =
        !isLoading;
    }
  }

  normalizeText(
    value
  ) {
    return value
      .toLocaleLowerCase(
        'lv'
      )
      .normalize('NFD')
      .replace(
        /\p{Diacritic}/gu,
        ''
      );
  }

  readCache() {
    try {
      const stored =
        sessionStorage.getItem(
          this.getCacheKey()
        );

      if (!stored) {
        return null;
      }

      const parsed =
        JSON.parse(
          stored
        );

      const cacheExpired =
        Date.now() -
          parsed.timestamp >
        PICKUP_CACHE_DURATION;

      if (
        !parsed.timestamp ||
        !Array.isArray(
          parsed.locations
        ) ||
        cacheExpired
      ) {
        sessionStorage.removeItem(
          this.getCacheKey()
        );

        return null;
      }

      return parsed.locations;

    } catch {
      return null;
    }
  }

  writeCache(
    locations
  ) {
    try {
      sessionStorage.setItem(
        this.getCacheKey(),
        JSON.stringify({
          timestamp:
            Date.now(),

          locations,
        })
      );
    } catch {
      // Selector still works if browser storage is unavailable.
    }
  }
}

if (
  !customElements.get(
    'cart-delivery-options'
  )
) {
  customElements.define(
    'cart-delivery-options',
    CartDeliveryOptions
  );
}